import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authAPI, employerAPI } from '../services/api';
import {
    AUTH_IDLE_TIMEOUT_MS,
    clearAuthStorage,
    getIdleExpiresAt,
    getLastActivityAt,
    getStoredRefreshToken,
    isIdleSessionExpired,
    markSessionActivity
} from '../utils/authSession';
import { isEmployerContactVerified, isEmployerRole } from '../utils/employerOnboarding';

const useAuthStore = create(
    persist(
        (set, get) => ({
            // State
            user: null,
            token: null,
            refreshToken: null,
            isAuthenticated: false,
            lastActivityAt: null,
            logoutReason: null,
            loading: false,
            verificationStatus: 'pending', // pending, in_review, verified, rejected
            kycDocuments: [],
            kycCompletion: null,

            // Actions
            login: async(credentials) => {
                set({ loading: true });
                try {
                    const response = await authAPI.login(credentials);

                    if (response.requiresLoginEmailVerification) {
                        set({
                            loading: false,
                            logoutReason: null
                        });

                        return {
                            success: true,
                            requiresLoginEmailVerification: true,
                            email: response.email,
                            challengeId: response.challengeId,
                            expiresInMinutes: response.expiresInMinutes,
                            message: response.message
                        };
                    }

                    // Extract token from response
                    const authToken = response.token || response.accessToken || response.data?.token || response.data?.accessToken;
                    const refreshToken = response.refreshToken || response.data?.refreshToken;

                    if (!authToken) {
                        throw new Error('No token received from server');
                    }

                    // Store token in localStorage for API service
                    localStorage.setItem('authToken', authToken);
                    localStorage.setItem('token', authToken);
                    if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
                    const lastActivityAt = markSessionActivity();

                    // Extract user data
                    const userData = response.user || response.data?.user || response;

                    // Extract employer data and add employerId to user
                    const employerData = response.employer || response.data?.employer;

                    let currentVerificationStatus = userData.verificationStatus || employerData?.verificationStatus || 'pending';

                    // Create enhanced user object with employer information
                    const enhancedUser = {
                        ...userData,
                        employerId: employerData?.id,
                        companyName: employerData?.companyName,
                        companySize: employerData?.companySize,
                        industry: employerData?.industry,
                        subscriptionPlan: employerData?.subscriptionPlan,
                        verifiedCompany: employerData?.verifiedCompany,
                        verificationStatus: currentVerificationStatus,
                        email2faEnabled: userData.email2faEnabled !== false,
                        permissions: userData.permissions || [],
                        employerRoleId: userData.employerRoleId,
                        employerRoleName: userData.employerRoleName,
                        employerRoleSlug: userData.employerRoleSlug,
                        employerRole: userData.employerRole,
                        isEmployerSuperAdmin: Boolean(userData.isEmployerSuperAdmin)
                    };

                    // Update Zustand store
                    set({
                        user: enhancedUser,
                        token: authToken,
                        refreshToken,
                        isAuthenticated: true,
                        lastActivityAt,
                        logoutReason: null,
                        loading: false,
                        verificationStatus: currentVerificationStatus
                    });

                    // If user is employer/hr_manager, fetch KYC status
                    if (isEmployerRole(enhancedUser.role) && isEmployerContactVerified(enhancedUser)) {
                        try {
                            const kycStatus = await employerAPI.getKycStatus();
                            currentVerificationStatus = kycStatus.verificationStatus || currentVerificationStatus;
                            set({
                                user: {
                                    ...enhancedUser,
                                    verificationStatus: currentVerificationStatus
                                },
                                verificationStatus: currentVerificationStatus,
                                kycDocuments: kycStatus.documents || [],
                                kycCompletion: kycStatus.kycCompletion
                            });
                        } catch (kycError) {
                            console.warn('Failed to load KYC status:', kycError);
                            // Don't fail login if KYC status fails to load
                        }
                    }
                    
                    return {
                        success: true,
                        user: {
                            ...enhancedUser,
                            verificationStatus: currentVerificationStatus
                        }
                    };

                } catch (error) {
                    console.error('=== LOGIN ERROR ===', error);
                    set({ loading: false });

                    let errorMessage = 'Login failed. Please try again.';
                    
                    if (typeof error === 'string') {
                        errorMessage = error;
                    } else if (error?.response?.data?.message) {
                        errorMessage = error.response.data.message;
                    } else if (error?.response?.data?.error) {
                        errorMessage = error.response.data.error;
                    } else if (error?.message) {
                        errorMessage = error.message;
                    } else if (error?.error) {
                        errorMessage = error.error;
                    }

                    return { success: false, error: errorMessage };
                }
            },

            verifyLoginEmail: async(payload) => {
                set({ loading: true });
                try {
                    const response = await authAPI.verifyLoginEmail(payload);
                    const authToken = response.token || response.accessToken || response.data?.token || response.data?.accessToken;
                    const refreshToken = response.refreshToken || response.data?.refreshToken;

                    if (!authToken) {
                        throw new Error('No token received from server');
                    }

                    localStorage.setItem('authToken', authToken);
                    localStorage.setItem('token', authToken);
                    if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
                    const lastActivityAt = markSessionActivity();

                    const userData = response.user || response.data?.user || response;
                    const employerData = response.employer || response.data?.employer;
                    let currentVerificationStatus = userData.verificationStatus || employerData?.verificationStatus || 'pending';

                    const enhancedUser = {
                        ...userData,
                        employerId: employerData?.id,
                        companyName: employerData?.companyName,
                        companySize: employerData?.companySize,
                        industry: employerData?.industry,
                        subscriptionPlan: employerData?.subscriptionPlan,
                        verifiedCompany: employerData?.verifiedCompany,
                        verificationStatus: currentVerificationStatus,
                        email2faEnabled: userData.email2faEnabled !== false,
                        permissions: userData.permissions || [],
                        employerRoleId: userData.employerRoleId,
                        employerRoleName: userData.employerRoleName,
                        employerRoleSlug: userData.employerRoleSlug,
                        employerRole: userData.employerRole,
                        isEmployerSuperAdmin: Boolean(userData.isEmployerSuperAdmin)
                    };

                    set({
                        user: enhancedUser,
                        token: authToken,
                        refreshToken,
                        isAuthenticated: true,
                        lastActivityAt,
                        logoutReason: null,
                        loading: false,
                        verificationStatus: currentVerificationStatus
                    });

                    if (isEmployerRole(enhancedUser.role) && isEmployerContactVerified(enhancedUser)) {
                        try {
                            const kycStatus = await employerAPI.getKycStatus();
                            currentVerificationStatus = kycStatus.verificationStatus || currentVerificationStatus;
                            set({
                                user: {
                                    ...enhancedUser,
                                    verificationStatus: currentVerificationStatus
                                },
                                verificationStatus: currentVerificationStatus,
                                kycDocuments: kycStatus.documents || [],
                                kycCompletion: kycStatus.kycCompletion
                            });
                        } catch (kycError) {
                            console.warn('Failed to load KYC status:', kycError);
                        }
                    }

                    return {
                        success: true,
                        user: {
                            ...enhancedUser,
                            verificationStatus: currentVerificationStatus
                        }
                    };
                } catch (error) {
                    set({ loading: false });

                    let errorMessage = 'Sign-in verification failed. Please try again.';
                    if (typeof error === 'string') {
                        errorMessage = error;
                    } else if (error?.response?.data?.message) {
                        errorMessage = error.response.data.message;
                    } else if (error?.response?.data?.error) {
                        errorMessage = error.response.data.error;
                    } else if (error?.message) {
                        errorMessage = error.message;
                    } else if (error?.error) {
                        errorMessage = error.error;
                    }

                    return { success: false, error: errorMessage };
                }
            },

            resendLoginEmailVerification: async(payload) => {
                try {
                    const response = await authAPI.resendLoginEmailVerification(payload);
                    return {
                        success: true,
                        email: response.email,
                        challengeId: response.challengeId,
                        expiresInMinutes: response.expiresInMinutes,
                        message: response.message
                    };
                } catch (error) {
                    return { success: false, error: error?.message || error?.error || 'Could not resend sign-in code.' };
                }
            },

            register: async(userData) => {
                set({ loading: true });
                try {
                    const response = await authAPI.register(userData);

                    const authToken = response.token || response.accessToken || response.data?.token || response.data?.accessToken;
                    const refreshToken = response.refreshToken || response.data?.refreshToken;
                    
                    if (authToken) {
                        localStorage.setItem('authToken', authToken);
                        localStorage.setItem('token', authToken);
                        if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
                        markSessionActivity();
                    }

                    const user = response.user || response.data?.user || response;
                    const employerData = response.employer || response.data?.employer;

                    const enhancedUser = {
                        ...user,
                        employerId: employerData?.id,
                        companyName: employerData?.companyName,
                        companySize: employerData?.companySize,
                        industry: employerData?.industry,
                        subscriptionPlan: employerData?.subscriptionPlan,
                        verifiedCompany: employerData?.verifiedCompany,
                        verificationStatus: user.verificationStatus || employerData?.verificationStatus || 'pending',
                        email2faEnabled: user.email2faEnabled !== false,
                        permissions: user.permissions || [],
                        employerRoleId: user.employerRoleId,
                        employerRoleName: user.employerRoleName,
                        employerRoleSlug: user.employerRoleSlug,
                        employerRole: user.employerRole,
                        isEmployerSuperAdmin: Boolean(user.isEmployerSuperAdmin)
                    };

                    set({
                        user: enhancedUser,
                        token: authToken,
                        refreshToken,
                        isAuthenticated: true,
                        lastActivityAt: authToken ? getLastActivityAt() : null,
                        logoutReason: null,
                        loading: false,
                        verificationStatus: 'pending' // Will be updated after KYC
                    });

                    return {
                        success: true,
                        user: enhancedUser,
                        profileEnrichment: response.profileEnrichment || response.data?.profileEnrichment || null
                    };
                } catch (error) {
                    set({ loading: false });
                    console.error('Registration error:', error);

                    let errorMessage = 'Registration failed. Please try again.';
                    
                    if (typeof error === 'string') {
                        errorMessage = error;
                    } else if (error?.response?.data?.message) {
                        errorMessage = error.response.data.message;
                    } else if (error?.response?.data?.error) {
                        errorMessage = error.response.data.error;
                    } else if (error?.response?.data?.errors) {
                        const errors = error.response.data.errors;
                        if (Array.isArray(errors) && errors.length > 0) {
                            errorMessage = errors[0].msg || errors[0].message || errors[0];
                        } else if (typeof errors === 'object') {
                            errorMessage = Object.values(errors)[0];
                        }
                    } else if (error?.message) {
                        errorMessage = error.message;
                    } else if (error?.error) {
                        errorMessage = error.error;
                    }

                    return { success: false, error: errorMessage };
                }
            },

            // Enhanced KYC actions
            updateKycStatus: async() => {
                try {
                    const { user } = get();
                    if (!user || !isEmployerRole(user.role) || !isEmployerContactVerified(user)) {
                        return;
                    }

                    const kycStatus = await employerAPI.getKycStatus();
                    set(state => ({
                        user: state.user && state.user.verificationStatus !== kycStatus.verificationStatus
                            ? { ...state.user, verificationStatus: kycStatus.verificationStatus }
                            : state.user,
                        verificationStatus: kycStatus.verificationStatus,
                        kycDocuments: kycStatus.documents || [],
                        kycCompletion: kycStatus.kycCompletion
                    }));

                    return kycStatus;
                } catch (error) {
                    console.error('Failed to update KYC status:', error);
                    throw error;
                }
            },

            submitKYC: async(documents) => {
                set({ loading: true });
                try {
                    const response = await authAPI.submitKYC(documents);
                    set(state => ({
                        user: state.user
                            ? { ...state.user, verificationStatus: 'in_review' }
                            : state.user,
                        kycDocuments: response.documents || response.data?.documents || [],
                        verificationStatus: 'in_review',
                        loading: false
                    }));
                    return { success: true };
                } catch (error) {
                    set({ loading: false });
                    console.error('KYC submission error:', error);

                    let errorMessage = 'KYC submission failed. Please try again.';
                    if (error?.response?.data?.message) {
                        errorMessage = error.response.data.message;
                    } else if (error?.response?.data?.error) {
                        errorMessage = error.response.data.error;
                    } else if (error?.message) {
                        errorMessage = error.message;
                    } else if (error?.error) {
                        errorMessage = error.error;
                    }

                    return { success: false, error: errorMessage };
                }
            },

            logout: (options = {}) => {
                const {
                    callApi = true,
                    reason = 'user_logout'
                } = options || {};
                const refreshTokenToRevoke = get().refreshToken || getStoredRefreshToken();

                if (callApi && refreshTokenToRevoke) {
                    authAPI.logout(refreshTokenToRevoke).catch(error => {
                        console.warn('Logout API request failed:', error?.message || error);
                    });
                }
                
                set({
                    user: null,
                    token: null,
                    refreshToken: null,
                    isAuthenticated: false,
                    lastActivityAt: null,
                    verificationStatus: 'pending',
                    kycDocuments: [],
                    kycCompletion: null,
                    logoutReason: reason
                });

                clearAuthStorage();
            },

            updateUser: (userData) => {
                set(state => ({
                    user: {...state.user, ...userData }
                }));
            },

            refreshAuthToken: async() => {
                try {
                    if (get().isSessionExpired()) {
                        get().logout({ reason: 'idle_timeout' });
                        return false;
                    }

                    const currentRefreshToken = get().refreshToken || getStoredRefreshToken();
                    if (!currentRefreshToken) {
                        throw new Error('No refresh token available');
                    }

                    const response = await authAPI.refreshToken(currentRefreshToken);
                    const token = response.token || response.accessToken || response.data?.token || response.data?.accessToken;
                    const refreshToken = response.refreshToken || response.data?.refreshToken || currentRefreshToken;
                    
                    if (token) {
                        set({ token, refreshToken });
                        localStorage.setItem('authToken', token);
                        localStorage.setItem('token', token);
                        localStorage.setItem('refreshToken', refreshToken);
                        console.log('✅ Token refreshed and stored');
                    }
                    
                    return true;
                } catch (error) {
                    console.error('Token refresh error:', error);
                    get().logout({ reason: 'auth_failed' });
                    return false;
                }
            },

            setToken: (token, refreshToken) => {
                if (token) {
                    set(state => ({
                        token,
                        refreshToken: refreshToken || state.refreshToken,
                        isAuthenticated: true
                    }));
                    localStorage.setItem('authToken', token);
                    localStorage.setItem('token', token);
                    if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
                    console.log('✅ Token set manually');
                } else {
                    get().logout({ reason: 'token_cleared' });
                }
            },

            touchSession: (timestamp = Date.now()) => {
                if (!get().isAuthenticated) return null;
                const lastActivityAt = markSessionActivity(timestamp);
                set({ lastActivityAt });
                return lastActivityAt;
            },

            syncLastActivity: (timestamp = getLastActivityAt()) => {
                set({ lastActivityAt: timestamp || null });
                return timestamp || null;
            },

            isSessionExpired: () => isIdleSessionExpired(),

            getSessionExpiresAt: () => getIdleExpiresAt(),

            ensureActiveSession: (reason = 'idle_timeout') => {
                if (get().isAuthenticated && get().isSessionExpired()) {
                    get().logout({ reason });
                    return false;
                }

                if (get().isAuthenticated && !getLastActivityAt()) {
                    get().touchSession();
                }

                return true;
            },

            getAuthState: () => {
                const state = get();
                return {
                    isAuthenticated: state.isAuthenticated,
                    hasToken: !!state.token,
                    hasRefreshToken: !!state.refreshToken || !!localStorage.getItem('refreshToken'),
                    hasUser: !!state.user,
                    userId: state.user?.id,
                    employerId: state.user?.employerId,
                    role: state.user?.role,
                    verificationStatus: state.verificationStatus,
                    kycComplete: state.kycCompletion?.is_complete || false,
                    localStorageToken: !!localStorage.getItem('authToken'),
                    idleTimeoutMs: AUTH_IDLE_TIMEOUT_MS,
                    lastActivityAt: state.lastActivityAt || getLastActivityAt(),
                    sessionExpiresAt: getIdleExpiresAt(),
                    sessionExpired: state.isSessionExpired()
                };
            },

            isUserVerified: () => {
                const { verificationStatus } = get();
                return verificationStatus === 'verified';
            },

            refreshVerificationStatus: async() => get().updateKycStatus(),

            setLoading: (loading) => set({ loading }),
            clearError: () => set({ error: null })
        }), {
            name: 'sanspath-auth',
            partialize: (state) => ({
                user: state.user,
                token: state.token,
                refreshToken: state.refreshToken,
                isAuthenticated: state.isAuthenticated,
                lastActivityAt: state.lastActivityAt,
                verificationStatus: state.verificationStatus,
                kycCompletion: state.kycCompletion
            })
        }
    )
);

export default useAuthStore;
